// src/context/AuthContext.js
import {
  browserLocalPersistence,
  browserSessionPersistence,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
  setPersistence,
  signInWithEmailAndPassword,
  signOut,
  updateProfile,
} from "firebase/auth";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { auth } from "../services/firebase";

export const AuthContext = createContext();

// Hook personalizado para usar el contexto de autenticación
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth debe usarse dentro de un AuthProvider");
  }
  return context;
};

/**
 * Traduce códigos de error de Firebase a mensajes amigables
 * @param {string} errorCode - Código de error de Firebase
 * @param {string} language - Idioma para el mensaje (es, ca, en)
 * @returns {string} Mensaje de error traducido
 */
const getAuthErrorMessage = (errorCode, language = "en") => {
  const errorMessages = {
    "auth/email-already-in-use": {
      en: "This email is already in use.",
      es: "Este correo ya está en uso.",
      ca: "Aquest correu ja està en ús.",
    },
    "auth/invalid-email": {
      en: "Please enter a valid email address.",
      es: "Por favor, introduce un correo electrónico válido.",
      ca: "Si us plau, introdueix un correu electrònic vàlid.",
    },
    "auth/user-disabled": {
      en: "This account has been disabled.",
      es: "Esta cuenta ha sido deshabilitada.",
      ca: "Aquest compte ha estat deshabilitat.",
    },
    "auth/user-not-found": {
      en: "No account found with this email.",
      es: "No se encontró ninguna cuenta con este correo electrónico.",
      ca: "No s'ha trobat cap compte amb aquest correu electrònic.",
    },
    "auth/wrong-password": {
      en: "Incorrect password.",
      es: "Contraseña incorrecta.",
      ca: "Contrasenya incorrecta.",
    },
    "auth/weak-password": {
      en: "Password should be at least 6 characters.",
      es: "La contraseña debe tener al menos 6 caracteres.",
      ca: "La contrasenya ha de tenir almenys 6 caràcters.",
    },
    "auth/too-many-requests": {
      en: "Too many login attempts. Please try again later.",
      es: "Demasiados intentos de inicio de sesión. Por favor, inténtalo de nuevo más tarde.",
      ca: "Massa intents d'inici de sessió. Si us plau, torna-ho a provar més tard.",
    },
  };

  // Si el idioma no está disponible o el código no existe, usar inglés como fallback
  if (!errorMessages[errorCode]) {
    return (
      errorMessages["default"]?.[language] ||
      "An error occurred. Please try again."
    );
  }

  return errorMessages[errorCode][language] || errorMessages[errorCode]["en"];
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [extendedUser, setExtendedUser] = useState(null);

  // Limpiar error después de un tiempo
  useEffect(() => {
    if (authError) {
      const timer = setTimeout(() => {
        setAuthError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [authError]);

  // Escuchar cambios en el estado de autenticación
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);

      // Si tenemos un usuario, enriquecer la información
      if (currentUser) {
        setExtendedUser({
          uid: currentUser.uid,
          email: currentUser.email,
          displayName: currentUser.displayName || "",
          photoURL: currentUser.photoURL || "",
          emailVerified: currentUser.emailVerified,
          isAnonymous: currentUser.isAnonymous,
          creationTime: currentUser.metadata?.creationTime,
          lastSignInTime: currentUser.metadata?.lastSignInTime,
        });
      } else {
        setExtendedUser(null);
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  /**
   * Inicia sesión con correo y contraseña
   * @param {string} email - Correo electrónico
   * @param {string} password - Contraseña
   * @param {boolean} remember - Si se debe recordar la sesión
   * @param {string} language - Idioma para mensajes de error
   */
  const login = useCallback(
    async (email, password, remember = false, language = "en") => {
      try {
        setAuthError(null);

        // Al inicio de la función login (línea 125):
        console.log(
          "LOGIN DIRECTO - recibió email:",
          email,
          "tipo:",
          typeof email,
        );

        // 1. Sanitización y validación básica
        if (!email) {
          const errorMessage = getAuthErrorMessage(
            "auth/invalid-email",
            language,
          );
          setAuthError({ code: "auth/invalid-email", message: errorMessage });
          throw new Error("Email vacío o no válido");
        }

        // 2. Conversión y limpieza
        const emailString =
          typeof email === "string" ? email.trim() : String(email).trim();

        // 3. Validación con regex más preciso
        // Esta expresión regular es más completa para validación de emails
        const emailRegex =
          /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

        if (!emailRegex.test(emailString)) {
          console.warn(`Formato de email inválido: "${emailString}"`);
          const errorMessage = getAuthErrorMessage(
            "auth/invalid-email",
            language,
          );
          setAuthError({ code: "auth/invalid-email", message: errorMessage });
          throw new Error(errorMessage);
        }

        // 4. Configurar persistencia
        const persistenceType = remember
          ? browserLocalPersistence
          : browserSessionPersistence;
        await setPersistence(auth, persistenceType);

        // 5. Iniciar sesión usando emailString (no email)
        console.log("Intentando login con email:", emailString);
        const userCredential = await signInWithEmailAndPassword(
          auth,
          emailString, // Usar la versión validada y sanitizada
          password,
        );

        // 6. Actualizar estado
        setUser(userCredential.user);
        return userCredential;
      } catch (error) {
        console.error("Login error:", error);
        // Mejorar el mensaje de error para depuración
        const errorDetail =
          error.code === "auth/invalid-email"
            ? `Email inválido: "${email}"`
            : error.message;
        console.error("Detalle del error:", errorDetail);

        const errorMessage = getAuthErrorMessage(
          error.code || "auth/unknown",
          language,
        );
        setAuthError({
          code: error.code || "auth/unknown",
          message: errorMessage,
        });
        throw error;
      }
    },
    [],
  );

  /**
   * Registra un nuevo usuario
   * @param {string} email - Correo electrónico
   * @param {string} password - Contraseña
   * @param {string} displayName - Nombre de usuario para el perfil
   * @param {string} language - Idioma para mensajes de error
   */
  const register = useCallback(
    async (email, password, displayName = "", language = "en") => {
      try {
        setAuthError(null);

        // Crear usuario
        const userCredential = await createUserWithEmailAndPassword(
          auth,
          email,
          password,
        );

        // Actualiza inmediatamente el estado del usuario
        // para evitar retrasos con el listener onAuthStateChanged
        setUser(userCredential.user);

        // Si se proporcionó un nombre, actualizar el perfil
        if (displayName) {
          await updateProfile(userCredential.user, { displayName });
        }

        return userCredential;
      } catch (error) {
        console.error("Registration error:", error);
        const errorMessage = getAuthErrorMessage(error.code, language);
        setAuthError({ code: error.code, message: errorMessage });
        throw error;
      }
    },
    [],
  );

  /**
   * Cierra la sesión actual
   */
  const logout = useCallback(async () => {
    try {
      setAuthError(null);
      await signOut(auth);
    } catch (error) {
      console.error("Logout error:", error);
      setAuthError({ code: error.code, message: "Error signing out" });
      throw error;
    }
  }, []);

  /**
   * Envía un correo de restablecimiento de contraseña
   * @param {string} email - Correo electrónico
   * @param {string} language - Idioma para mensajes de error
   */
  const resetPassword = useCallback(async (email, language = "en") => {
    try {
      setAuthError(null);
      await sendPasswordResetEmail(auth, email);
      return true;
    } catch (error) {
      console.error("Password reset error:", error);
      const errorMessage = getAuthErrorMessage(error.code, language);
      setAuthError({ code: error.code, message: errorMessage });
      throw error;
    }
  }, []);

  /**
   * Envía un correo de verificación al usuario actual
   * @param {string} language - Idioma para mensajes de error
   */
  const verifyEmail = useCallback(async (language = "en") => {
    try {
      setAuthError(null);
      if (!auth.currentUser) {
        throw new Error("No authenticated user");
      }
      await sendEmailVerification(auth.currentUser);
      return true;
    } catch (error) {
      console.error("Email verification error:", error);
      const errorMessage = getAuthErrorMessage(error.code, language);
      setAuthError({ code: error.code, message: errorMessage });
      throw error;
    }
  }, []);

  /**
   * Actualiza el perfil del usuario actual
   * @param {Object} profileData - Datos del perfil a actualizar
   * @param {string} language - Idioma para mensajes de error
   */
  const updateUserProfile = useCallback(
    async (profileData, language = "en") => {
      try {
        setAuthError(null);
        if (!auth.currentUser) {
          throw new Error("No authenticated user");
        }
        await updateProfile(auth.currentUser, profileData);

        // Actualizar estado local con los nuevos datos
        setExtendedUser((prev) => ({
          ...prev,
          ...profileData,
        }));

        return true;
      } catch (error) {
        console.error("Profile update error:", error);
        const errorMessage = getAuthErrorMessage(error.code, language);
        setAuthError({ code: error.code, message: errorMessage });
        throw error;
      }
    },
    [],
  );

  return (
    <AuthContext.Provider
      value={{
        user,
        currentUser: user, // AÑADIR ESTA LÍNEA - mapea user a currentUser
        extendedUser,
        loading,
        isLoading: loading, // Alias para compatibilidad con App.js
        authError,
        setUser,
        login,
        register,
        logout,
        resetPassword,
        verifyEmail,
        updateUserProfile,
        clearError: () => setAuthError(null),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
